import { Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { ZodError } from 'zod';
import CopyToClipboard from 'react-copy-to-clipboard';
import axios from 'axios';

import { ColumnSpec } from '../../components/common/DataTable/types';
import { User } from '../../components/Dashboard/types';
import getLocalStorageData, { setLocalStorageData } from '../../utils/token';
import getAPIUrl from '../../config';
import { getApi, putApiWithAuth } from '../../utils/apiCalls';
import MyProfileDetails from '../../components/MyProfile/ProfileDetails';
import EditMyProfile from '../../components/MyProfile/EditProfile';
import { UserData } from '../../components/MyProfile/ProfileDetails/types';
import styles from './styles';
import getPendingRequests, { getResolvedRequests } from './api';
import { SortDirection } from '../../components/common/DataTable/sortable-header';
import editProfileSchema from '../../utils/schemas';
import { INewMemberRequests } from './types';

import NewMemberRequestManagement from '../../components/NewMemberRequestManagement';
import UserAvatar from '../../components/common/UserAvatar/UserAvatar';
import VeriyfMemberRequest from '../VerifyMemberRequest';
import { getPartners } from '../PartnerAndMembers/api';
import { IPartner } from '../../components/PatrnerAndMembersManagment/types';
import { useToast } from '../../components/common/ToasterContext';
import { showLoaderAfterTimeout, trimFormData } from '../../utils/helpers';

const NewMemberRequest = () => {
    const userData = getLocalStorageData();

    const [pendingAndResolved, setPendingAndResolved] = useState<number>(0);

    const [pendingData, setPendingData] = useState<INewMemberRequests[]>([]);
    const [pendingCount, setPendingCount] = useState<number>(0);

    const [resolvedCount, setResolvedCount] = useState<number>(0);
    const [resolvedData, setResolvedData] = useState<INewMemberRequests[]>([]);

    const [pendingHasMore, setPendingHasMore] = useState(true);
    const [resolvedHasMore, setResolvedHasMore] = useState(true);

    const [pendingCurrentPage, setPendingCurrentPage] = useState<number>(1);
    const [resolvedCurrentPage, setResolvedCurrentPage] = useState<number>(1);

    const [openProfileDetails, setOpenProfileDetails] = useState(false);
    const [editProfileDetails, setEditProfileDetails] = useState(false);

    const [openVerifyMemberRequest, setOpenVerifyMemberRequest] = useState<boolean>(false);
    const [verifyMemberRequestData, setVerifyMemberRequestData] = useState<INewMemberRequests>();

    const [partnerList, setPartnerList] = useState<IPartner[]>([]);

    const [selectedRoleId, setSelectedRoleId] = useState<number>(0);
    const [selectedImage, setSelectedImage] = useState<string | ArrayBuffer | null | undefined>();
    const [selectedFile, setSelectedFile] = useState<File>();

    const [userInfo, setUserInfo] = useState<UserData>({
        ...userData,
        password: '',
        repeatPassword: '',
        roleId: '',
    });

    const [roles, setRoles] = useState<Array<{ name: string; value: number }>>([]);

    const [pendingSearchText, setPendingSearchText] = useState('');
    const [resolvedSearchText, setResolvedSearchText] = useState('');

    const [employerId, setEmployerId] = useState<string>('');

    const [pendingSortKey, setPendingSortkey] = useState<string>('firstname');
    const [pendingSortOrder, setPendingSortOrder] = useState<string>('asc');

    const [resolvedSortKey, setResolvedSortkey] = useState<string>('firstname');
    const [resolvedSortOrder, setResolvedSortOrder] = useState<string>('asc');
    const toast = useToast();
    const [formErrors, setFormErrors] = useState<{
        [key: string]: Array<string>;
    }>();

    const [loader, setLoader] = useState(true);
    const size = 10;
    const [reload, setReload] = useState(false);
    const [submitting, setSubmitting] = useState<boolean>(false);

    const user: User = {
        name: `${userData.firstname} ${userData.lastname}`,
        profileImg: userData.signedImageUrl,
    };

    const onPendingSearchChange = (text: string) => {
        setPendingSearchText(text);
        setPendingCurrentPage(1);
    };

    const onResolvedSearchChange = (text: string) => {
        setResolvedSearchText(text);
        setResolvedCurrentPage(1);
    };

    const pendingColumnSpec: ColumnSpec<INewMemberRequests>[] = [
        {
            name: 'firstname',
            title: 'First Name',
            cellProperty: 'firstname',
            cellRenderer: (pending: INewMemberRequests) => (
                <Typography sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }} component="div">
                    <Typography component="span" sx={styles.title}>
                        {pending.firstname}
                    </Typography>
                </Typography>
            ),
        },
        {
            name: 'lastname',
            title: 'Last Name',
            cellProperty: 'lastname',
            cellRenderer: (pending: INewMemberRequests) => (
                <Typography sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }} component="div">
                    <Typography component="span" sx={styles.lastName}>
                        {pending.lastname}
                    </Typography>
                </Typography>
            ),
        },
        {
            name: 'email',
            title: 'Email',
            cellProperty: 'email',
            cellRenderer: (pending: INewMemberRequests) => (
                <CopyToClipboard text={pending.email}>
                    <Typography sx={styles.email}>
                        <img src="/images/copy-to-clipboard.png" alt="Copy To clip board" />
                        <Typography component="span" sx={styles.rowLabel}>
                            {pending.email}
                        </Typography>
                    </Typography>
                </CopyToClipboard>
            ),
        },
        {
            name: 'employerName',
            title: 'Partner',
            cellProperty: 'employerName',
            cellRenderer: (pending: INewMemberRequests) => (
                <Typography sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }} component="div">
                    <Typography component="span" sx={styles.lastName}>
                        {pending.employerName}
                    </Typography>
                </Typography>
            ),
        },
        {
            name: 'firstname',
            title: 'Action',
            cellProperty: 'firstname',
            cellRenderer: (pending: INewMemberRequests) => (
                <Typography sx={styles.email}>
                    <img
                        alt="status"
                        src={
                            openVerifyMemberRequest && pending.id === verifyMemberRequestData?.id
                                ? '/images/pending_verify_active.svg'
                                : '/images/pending_verify_inactive.svg'
                        }
                    />
                    <Typography id="pending_verify" component="span" sx={styles.verify}>
                        Verify
                    </Typography>
                </Typography>
            ),
        },
    ];

    const resolvedColumnSpec: ColumnSpec<INewMemberRequests>[] = [
        {
            name: 'firstname',
            title: 'First Name',
            cellProperty: 'firstname',
            cellRenderer: (resolved: INewMemberRequests) => (
                <Typography sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }} component="div">
                    <Typography component="span" sx={styles.title}>
                        {resolved.firstname}
                    </Typography>
                </Typography>
            ),
        },
        {
            name: 'lastname',
            title: 'Last Name',
            cellProperty: 'lastname',
            cellRenderer: (resolved: INewMemberRequests) => (
                <Typography sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }} component="div">
                    <Typography component="span" sx={styles.lastName}>
                        {resolved.lastname}
                    </Typography>
                </Typography>
            ),
        },
        {
            name: 'email',
            title: 'Email',
            cellProperty: 'email',
            cellRenderer: (resolved: INewMemberRequests) => (
                <CopyToClipboard text={resolved.email}>
                    <Typography sx={styles.email}>
                        <img src="/images/copy-to-clipboard.png" alt="Copy To clip board" />
                        <Typography component="span" sx={styles.rowLabel}>
                            {resolved.email}
                        </Typography>
                    </Typography>
                </CopyToClipboard>
            ),
        },
        {
            name: 'employerName',
            title: 'Partner',
            cellProperty: 'employerName',
            cellRenderer: (resolved: INewMemberRequests) => (
                <Typography sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }} component="div">
                    {resolved.employer && resolved.employerName ? (
                        <UserAvatar
                            name={`${resolved.employerName}`}
                            size={32}
                            source={resolved.employer?.logoUrl || ''}
                            radius="20px"
                        />
                    ) : null}
                    {resolved.employer && resolved.employerName ? (
                        <Typography component="span" sx={styles.approvedPartner}>
                            {resolved.employerName}
                        </Typography>
                    ) : (
                        <Typography component="span" sx={styles.deniedPartner}>
                            Partner Name (not selected)
                        </Typography>
                    )}
                </Typography>
            ),
        },
        {
            name: 'Review',
            title: 'Status',
            cellProperty: 'status',
            cellRenderer: (resolved: INewMemberRequests) => (
                <Typography sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }} component="div">
                    <Typography
                        component="span"
                        sx={resolved.status === 'Approved' ? styles.statusApproved : styles.statusDenied}
                    >
                        {resolved.status}
                    </Typography>
                </Typography>
            ),
        },
    ];

    useEffect(() => {
        getApi(`${getAPIUrl()}/admins/admin-roles`).then((res) => {
            const rolesData = res.data.data.data.map((r: { role: string; id: string }) => ({
                name: r.role,
                value: Number(r.id),
            }));
            setRoles(rolesData);
            const roleData = rolesData.find((role: { name: string; value: number }) => role.name === userData.roleName);
            setSelectedRoleId(roleData?.value);
            setUserInfo({ ...userInfo, roleId: String(roleData?.value) });
        });

        getPartners({
            currentPage: 1,
            partnerSearchText: '',
            partnerSize: 1000000,
            selectedPartnerPeriod: '',
            selectedPartnerStatus: '',
            order: 'asc',
            sort: 'name',
        }).then((res) => {
            setPartnerList(res.data.data.rows);
        });
    }, []);

    useEffect(() => {
        setLoader(true);
        if (pendingAndResolved === 1) {
            getResolvedRequests({
                resolvedPage: resolvedCurrentPage,
                resolvedSearchText,
                resolvedSize: size,
                sort: resolvedSortKey,
                order: resolvedSortOrder,
            })
                .then((res) => {
                    setResolvedData(res.data.data.rows);
                    setResolvedCount(res.data.data.count);
                    showLoaderAfterTimeout(setLoader);
                })
                .catch((_error) => {
                    showLoaderAfterTimeout(setLoader);
                });
        } else {
            getPendingRequests({
                pendingPage: pendingCurrentPage,
                pendingSearchText,
                pendingSize: size,
                sort: pendingSortKey,
                order: pendingSortOrder,
            })
                .then((res) => {
                    setPendingData(res.data.data.rows);
                    setPendingCount(res.data.data.count);
                    showLoaderAfterTimeout(setLoader);
                })
                .catch((_error) => {
                    showLoaderAfterTimeout(setLoader);
                });

            if (resolvedCount === 0 || openVerifyMemberRequest) {
                getResolvedRequests({
                    resolvedPage: resolvedCurrentPage,
                    resolvedSearchText,
                    resolvedSize: size,
                    sort: resolvedSortKey,
                    order: resolvedSortOrder,
                })
                    .then((res) => {
                        setResolvedData(res.data.data.rows);
                        setResolvedCount(res.data.data.count);
                        showLoaderAfterTimeout(setLoader);
                    })
                    .catch((_error) => {
                        showLoaderAfterTimeout(setLoader);
                    });
            }
        }
    }, [pendingAndResolved, reload, pendingSearchText, resolvedSearchText]);

    const resolvedLoadMore = () => {
        const loadItems = async () => {
            if (size * (resolvedCurrentPage - 1) < resolvedCount) {
                const res = await getResolvedRequests({
                    resolvedPage: resolvedCurrentPage + 1,
                    resolvedSearchText,
                    resolvedSize: size,
                    sort: resolvedSortKey,
                    order: resolvedSortOrder,
                });
                if (!axios.isAxiosError(res)) {
                    const rData = [...resolvedData, ...res.data.data.rows];
                    if (!(rData.length === resolvedCount)) {
                        setResolvedCurrentPage(resolvedCurrentPage + 1);
                    }
                    setResolvedData(rData);
                    showLoaderAfterTimeout(setLoader);
                }
            }
        };
        setResolvedHasMore(!(resolvedData.length === resolvedCount));
        loadItems();
    };

    const pendingLoadMore = () => {
        const loadItems = async () => {
            if (size * (pendingCurrentPage - 1) < pendingCount) {
                const res = await getPendingRequests({
                    pendingPage: pendingCurrentPage + 1,
                    pendingSearchText,
                    pendingSize: size,
                    sort: pendingSortKey,
                    order: pendingSortOrder,
                });
                if (!axios.isAxiosError(res)) {
                    const pData = [...pendingData, ...res.data.data.rows];
                    if (!(pData.length === pendingCount)) {
                        setPendingCurrentPage(pendingCurrentPage + 1);
                    }
                    setPendingData(pData);
                    showLoaderAfterTimeout(setLoader);
                }
            }
        };
        setPendingHasMore(!(pendingData.length === pendingCount));
        loadItems();
    };

    const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        try {
            setSubmitting(true);
            editProfileSchema.parse(trimFormData(userInfo));
            const formData = new FormData();
            formData.append('roleId', String(selectedRoleId));
            formData.append('firstname', userInfo.firstname);
            formData.append('lastname', userInfo.lastname);
            formData.append('imageFile', selectedFile as any);
            if (userInfo.password) {
                formData.append('password', String(userInfo.password || ''));
            }
            if (userInfo.repeatPassword) {
                formData.append('repeatPassword', String(userInfo.repeatPassword || ''));
            }

            const result = await putApiWithAuth(`${getAPIUrl()}/admins/edit-admin`, formData);
            setUserInfo({
                ...userInfo,
                email: result.data.data.data.email,
                firstname: result.data.data.data.firstname,
                lastname: result.data.data.data.lastname,
                profileImageUrl: result.data.data.data.profileImageUrl,
                signedImageUrl: result.data.data.data.signedImageUrl,
                roleName: roles.find((r) => r.value === Number(result.data.data.data.roleId))?.name || '',
                password: '',
                repeatPassword: '',
            });
            setLocalStorageData({
                ...userData,
                adminRole: {
                    role: roles.find((r) => r.value === Number(result.data.data.data.roleId))?.name || '',
                },
                email: result.data.data.data.email,
                firstname: result.data.data.data.firstname,
                lastname: result.data.data.data.lastname,
                profileImageUrl: result.data.data.data.profileImageUrl,
                signedImageUrl: result.data.data.data.signedImageUrl,
            });
            setSelectedRoleId(result.data.data.data.roleId);
            setEditProfileDetails(false);
            setOpenProfileDetails(false);
            if (toast) {
                toast.onToggleSnackbar();
                toast.onMessage('Request Processeed');
                toast.onSuccess();
            }
        } catch (error: any) {
            if (error instanceof ZodError) {
                setFormErrors(error.flatten().fieldErrors);
            } else if (toast) {
                toast.onToggleSnackbar();
                toast.onMessage(error.response.data.errors);
                toast.onError();
            }
        } finally {
            setSubmitting(false);
        }
    };

    const handleInput = (e: React.ChangeEvent<HTMLInputElement>): void => {
        const { value, name } = e.target;
        if (name === 'roleId') {
            setSelectedRoleId(Number(value));
        }
        setUserInfo({
            ...userInfo,
            [name]: String(value),
        });
    };

    const handleChangeAvatar = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            setSelectedFile(e.target.files[0]);
            const reader = new FileReader();
            reader.onload = (event) => {
                setSelectedImage(event?.target?.result);
            };
            reader.readAsDataURL(e.target.files[0]);
        }
    };

    const pendingSortData = async (sortKey: string, direction: SortDirection) => {
        const res = await getPendingRequests({
            pendingSize: size,
            pendingPage: 1,
            pendingSearchText: '',
            sort: sortKey,
            order: direction === 0 ? 'desc' : 'asc',
        });
        if (!axios.isAxiosError(res)) {
            setPendingData([...res.data.data.rows]);
            setPendingCount(res.data.data.count);
            setPendingCurrentPage(1);
            setPendingHasMore(true);
            setPendingSortkey(sortKey);
            setPendingSortOrder(direction === 0 ? 'desc' : 'asc');
            showLoaderAfterTimeout(setLoader);
        }
    };

    const resolvedSortData = async (sortKey: string, direction: SortDirection) => {
        const res = await getResolvedRequests({
            resolvedPage: 1,
            resolvedSearchText: '',
            resolvedSize: size,
            sort: sortKey,
            order: direction === 0 ? 'desc' : 'asc',
        });
        if (!axios.isAxiosError(res)) {
            setLoader(false);
            setResolvedData([...res.data.data.rows]);
            setResolvedCount(res.data.data.count);
            setResolvedCurrentPage(1);
            setResolvedHasMore(true);
            setResolvedSortkey(sortKey);
            setResolvedSortOrder(direction === 0 ? 'desc' : 'asc');
        }
    };

    const handleCloseMyProfile = (value: boolean) => {
        setUserInfo({
            ...userData,
            password: '',
            repeatPassword: '',
            roleId: String(
                roles.find((role: { name: string; value: number }) => role.name === userData.roleName)?.value,
            ),
        });
        setEditProfileDetails(value);
        setFormErrors(undefined);
    };

    const handleRowClick = (rowData: INewMemberRequests) => {
        setVerifyMemberRequestData(rowData);
        setOpenVerifyMemberRequest(true);
    };

    return (
        <>
            <NewMemberRequestManagement
                pendingColumnSpec={pendingColumnSpec}
                resolvedColumnSpec={resolvedColumnSpec}
                pendingData={pendingData}
                resolvedData={resolvedData}
                user={user}
                loader={loader}
                pendingLoadMore={pendingLoadMore}
                resolvedLoadMore={resolvedLoadMore}
                pendingHasMore={pendingHasMore}
                resolvedHasMore={resolvedHasMore}
                onPendingSearchChange={onPendingSearchChange}
                onResolvedSearchChange={onResolvedSearchChange}
                openProfileDetails={openProfileDetails}
                setOpenProfileDetails={setOpenProfileDetails}
                setPendingAndResolved={setPendingAndResolved}
                resolvedCount={resolvedCount}
                setReload={setReload}
                pendingSortData={pendingSortData}
                resolvedSortData={resolvedSortData}
                setPendingCurrentPage={setPendingCurrentPage}
                reload={reload}
                pendingAndResolved={pendingAndResolved}
                pendingCount={pendingCount}
                handleRowClick={handleRowClick}
            />
            <MyProfileDetails
                openProfileDetails={openProfileDetails}
                setOpenProfileDetails={setOpenProfileDetails}
                setEditProfileDetails={setEditProfileDetails}
                userData={userData}
            />
            <EditMyProfile
                editProfileDetails={editProfileDetails}
                userInfo={userInfo}
                handleInput={handleInput}
                handleSubmit={onSubmit}
                roles={roles}
                selectedRoleId={String(selectedRoleId)}
                handleChangeAvatar={handleChangeAvatar}
                selectedImage={selectedImage}
                formErrors={formErrors}
                handleCloseMyProfile={handleCloseMyProfile}
                submitting={submitting}
            />
            <VeriyfMemberRequest
                openVerifyMemberRequest={openVerifyMemberRequest}
                setOpenVerifyMemberRequest={setOpenVerifyMemberRequest}
                verifyMemberRequestData={verifyMemberRequestData}
                employerId={employerId}
                setEmployerId={setEmployerId}
                setReload={setReload}
                reload={reload}
                partnerList={partnerList}
            />
        </>
    );
};
export default NewMemberRequest;
